import { Stepper as AdsStepper } from '@qbe/ads-core';
import toNumber from 'lodash/toNumber';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useAppDispatch, useAppSelector, useStep } from '~/hooks/useSelectors';
import { selectIsReferralVelocity } from '~/selectors/selectAppDetails';
import { navigateAction } from '~/thunks/navigateAction';
import { Step } from '~/types/enums';

import { steps, toStepCategory } from './Stepper.mapping';

// The step index threshold after which membership affects the step numbering
const MEMBERSHIP_STEP_THRESHOLD = 12;

const Stepper = () => {
  const hasMembership = useAppSelector(selectIsReferralVelocity);

  /**
   * Determines the list of steps to display in the stepper component.
   * If `hasMembershipStep` is true, all steps are included.
   * Otherwise, filters out the step with type `Step.Membership`.
   *
   * @remarks
   * This logic is used to conditionally include or exclude the membership step based on the application's requirements.
   *
   * @param hasMembership - A boolean indicating whether the membership step should be included.
   * @param steps - An array of step objects, each containing a `step` property.
   * @returns The filtered array of steps to be displayed.
   */
  const visibleSteps = hasMembership
    ? steps
    : steps.filter((step) => step.step !== Step.Membership);

  const { currentStep } = useStep();
  const activeStep = useMemo(() => {
    const toStepCategoryMap = !hasMembership
      ? toStepCategory
      : Object.fromEntries(
          Object.entries(toStepCategory).map(([key, value]) => [
            key,
            Number(key) >= MEMBERSHIP_STEP_THRESHOLD ? value + 1 : value,
          ])
        );

    return toStepCategoryMap[currentStep];
  }, [currentStep, hasMembership]);
  const isStepperDisabled = useMemo(
    () => [Step.Review, Step.Payments, Step.Confirmation].includes(currentStep),
    [currentStep]
  );

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const goToStep = useCallback(
    (idx: number | string) => {
      const numIdx = toNumber(idx);
      const lessThanActiveStep = numIdx < activeStep;

      if (lessThanActiveStep || currentStep === Step.Payments) {
        const { route } = visibleSteps[numIdx];
        dispatch(
          navigateAction({
            to: route,
            navigate,
          })
        );
      }
    },
    [activeStep, currentStep, dispatch, navigate, visibleSteps]
  );

  const stepperSteps = useMemo(
    () =>
      visibleSteps.map((s, index) => {
        const lessThanActiveStep = index < activeStep;
        return {
          label: s.label,
          id: s.step,
          // on payments (review and buy step) can click "review and buy" icon and navigate to "review" (which is previous step but shares same circle with payments)
          disabled: isStepperDisabled && lessThanActiveStep, // don't disable active step
          completed: lessThanActiveStep,
          'data-track': `header: step - ${s.label}`,
        };
      }),
    [activeStep, isStepperDisabled, visibleSteps]
  );

  if (typeof activeStep === 'undefined') return null;

  return (
    <AdsStepper
      activeStep={activeStep}
      steps={stepperSteps}
      goToStep={goToStep}
      variant="ondark"
      nonLinear
    />
  );
};

export default Stepper;
